// ==== Build / geometry logic (FONTS, THICKNESS, rail + engraved + connected builders) ====
(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Config
  // ---------------------------------------------------------------
  var RATE = 7.5;
  var MAX_CHARS = 10;
  var ALLOWED = /^[A-Za-z0-9 &'\-.,!?]*$/;
  // UNITS_PER_MM converts scene units to a physical mm figure. It is used to
  // drive the metal-thickness geometry (a real spec: 0.70/0.80/1.0mm — see
  // THICKNESS below) so the render's depth is proportionally correct relative
  // to itself. IMPORTANT: this 3.1 figure was reverse-engineered years ago to
  // make an on-screen "~Xmm wide" label produce a plausible-looking number for
  // a default plate — it was never calibrated against an actual measured
  // production piece. That label has been removed (2026-09-14 — it risked
  // showing customers a width number that wasn't real, e.g. "~17mm" for a
  // wearable nameplate). This constant is still safe to use for *relative*
  // scaling between thickness tiers, but do not resurrect any on-screen "Xmm"
  // width/size claim from it without first calibrating it against a real
  // measured sample (see project memory: "Sizing display" note).
  var UNITS_PER_MM = 3.1;

  var FONTS = {
    plex: {
      key: 'plex',
      label: 'Technical', data: window.__PLEX_FONT_DATA, size: 21, depth: 7,
      bevelThickness: 1, bevelSize: 0.6, curveSegments: 5,
      note: 'Technical — IBM Plex Mono Bold, the brand’s system face. Even monospace strokes, cuts clean at any size.'
    },
    barlow: {
      key: 'barlow',
      label: 'Barlow', data: window.__BARLOW_FONT_DATA, size: 22, depth: 7,
      bevelThickness: 0.9, bevelSize: 0.55, curveSegments: 4,
      note: 'Barlow — grotesque sans in 600 SemiBold. Clean, rounded, confident weight.'
    },
    pirataone: {
      key: 'pirataone',
      label: 'Pirata One', data: window.__PIRATA_ONE_FONT_DATA, size: 24, depth: 7,
      bevelThickness: 0.9, bevelSize: 0.55, curveSegments: 5,
      note: 'Pirata One — gothic blackletter. Dramatic, old-world cut.'
    },
    zillaslab: {
      key: 'zillaslab',
      label: 'Zilla Slab', data: window.__ZILLA_SLAB_FONT_DATA, size: 24, depth: 7,
      bevelThickness: 0.8, bevelSize: 0.5, curveSegments: 5,
      note: 'Zilla Slab — vintage slab-serif. Warm and grounded.'
    },
    italianno: {
      key: 'italianno',
      label: 'Italianno', data: window.__ITALIANNO_FONT_DATA, size: 30, depth: 6,
      bevelThickness: 0.7, bevelSize: 0.4, curveSegments: 6,
      // 2026-09-22 — the font-wide `connectedGap`/`connectedGapByChar`/
      // `connectedPairGap` tuning that used to live here (four separate
      // passes: a flat -10, a per-character cap for "O", then exact
      // overrides for "IA" and "ZO") is gone. Connected Letters now fits
      // every pair from the real glyph ink (see buildConnectedLetters()),
      // so it no longer needs a hand-tuned table per font — this entry
      // just supplies size/depth/bevel like every other font below.
      note: 'Italianno — elaborate flowing script with dramatic long connectors.'
    },
    cinzel: {
      key: 'cinzel',
      label: 'Cinzel', data: window.__CINZEL_FONT_DATA, size: 20, depth: 7,
      bevelThickness: 0.9, bevelSize: 0.5, curveSegments: 5,
      note: 'Cinzel — Roman inscriptional capitals, 700 Bold. Classic engraved-luxury cut.'
    }
  };

  var FINISH = {
    polished: { label: 'Polished' },
    brushed: { label: 'Brushed' }
  };

  // Metal thickness — no longer a customer-facing choice (2026-09-21: "let's
  // remove metal thickness option, as it will help us removing variants").
  // Every plate is cut at a single fixed real-world gauge. `mm` is the real
  // spec (label, pricing, anything customer-facing) and stays 0.70mm.
  //
  // `displayMm` (2026-09-23 — "we will call it 0.70mm but for display let's
  // use 0.45mm") is a SEPARATE, render-only depth used only to compute
  // plateDepth in the three builders below. The actual product is still cut
  // at 0.70mm; only the on-screen 3D preview renders thinner. Every geometry
  // call site reads `displayMm || mm` (see displayThicknessMm() below) so
  // nothing customer-facing (label text, pricing) ever sees this value —
  // if a real gauge change is ever wanted instead, drop displayMm and
  // change `mm` itself.
  var THICKNESS = {
    standard: { label: '0.70mm', mm: 0.70, displayMm: 0.45, price: 0 }
  };

  function displayThicknessMm(thicknessDef) {
    return (thicknessDef.displayMm != null ? thicknessDef.displayMm : thicknessDef.mm);
  }

  // ---------------------------------------------------------------
  // DOM refs
  // ---------------------------------------------------------------
  var canvas = document.getElementById('gl');
  var stageWrap = document.getElementById('stagewrap');
  var input = document.getElementById('nm-name');
  var charcount = document.getElementById('nm-charcount');
  var fontBtns = document.querySelectorAll('#nm-font-row .opt');
  var finishBtns = document.querySelectorAll('#nm-finish-row .opt');
  var constructionRowEl = document.getElementById('nm-construction-row');
  var fontNote = document.getElementById('nm-font-note');
  var letterCountEl = document.getElementById('nm-letter-count');
  var priceEl = document.getElementById('nm-price');
  var cta = document.getElementById('nm-cta');
  var specFinishLabel = document.getElementById('spec-finish-label');
  var dimLine = document.getElementById('dim-line');
  var loadingEl = document.getElementById('gl-loading');
  var fpsEl = document.getElementById('gl-fps');

  var currentFontKey = 'plex';
  var currentFinishKey = 'polished';

  // ---------------------------------------------------------------
  // Renderer / scene / camera
  // ---------------------------------------------------------------
  var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: false, preserveDrawingBuffer: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  var scene = new THREE.Scene();
  scene.background = new THREE.Color(0xffffff);

  var camera = new THREE.PerspectiveCamera(32, 1, 0.1, 2000);
  camera.position.set(0, 8, 200);

  var controls = new THREE.OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.minDistance = 80;
  controls.maxDistance = 340;
  controls.minPolarAngle = Math.PI * 0.28;
  controls.maxPolarAngle = Math.PI * 0.72;
  controls.autoRotate = true;
  controls.autoRotateSpeed = 1.6;
  controls.target.set(0, 0, 0);

  var idleTimer = null;
  var cameraFitActive = true; // auto-glides to frame the plate after a rebuild; a manual drag/zoom hands control back to the visitor
  function pauseAutoRotate() {
    controls.autoRotate = false;
    cameraFitActive = false;
    clearTimeout(idleTimer);
    idleTimer = setTimeout(function () { controls.autoRotate = true; }, 2600);
  }
  renderer.domElement.addEventListener('pointerdown', pauseAutoRotate);
  renderer.domElement.addEventListener('wheel', pauseAutoRotate, { passive: true });

  // ---------------------------------------------------------------
  // Lighting rig — was split into a "Technical Spec" and a "Studio
  // Spotlight" rig, swapped on tab click. The Studio Spotlight tab is
  // gone, and the red accent point light that used to belong to that
  // rig has been removed too (per "keeping it simple") — this is now
  // a plain, neutral key/fill/ambient rig with no colour cast.
  // ---------------------------------------------------------------
  var lightsSpec = new THREE.Group();
  var keySpec = new THREE.DirectionalLight(0xffffff, 2.0);
  keySpec.position.set(60, 110, 90);
  keySpec.castShadow = true;
  keySpec.shadow.mapSize.set(1024, 1024);
  keySpec.shadow.camera.left = -80; keySpec.shadow.camera.right = 80;
  keySpec.shadow.camera.top = 80; keySpec.shadow.camera.bottom = -80;
  lightsSpec.add(keySpec);
  var fillSpec = new THREE.DirectionalLight(0xffffff, 0.6);
  fillSpec.position.set(-90, 40, 60);
  lightsSpec.add(fillSpec);
  var ambSpec = new THREE.AmbientLight(0xffffff, 0.35);
  lightsSpec.add(ambSpec);

  scene.add(lightsSpec);

  // ---------------------------------------------------------------
  // Procedural studio environment map (no external HDR needed)
  // ---------------------------------------------------------------
  function buildEnvTexture() {
    var w = 512, h = 256;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d');
    ctx.fillStyle = '#050505';
    ctx.fillRect(0, 0, w, h);
    function blob(x, y, r, alpha) {
      var g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,255,255,' + alpha + ')');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
    }
    // soft "softbox" highlights, arranged so metal picks up believable streaks
    blob(w * 0.28, h * 0.30, 130, 0.9);
    blob(w * 0.74, h * 0.28, 100, 0.55);
    blob(w * 0.50, h * 0.66, 160, 0.35);
    blob(w * 0.10, h * 0.75, 70, 0.25);
    blob(w * 0.90, h * 0.72, 90, 0.3);
    // thin bright seam lines (mimic softbox edges / studio strip lights)
    ctx.strokeStyle = 'rgba(255,255,255,0.5)';
    ctx.lineWidth = 2;
    for (var i = 0; i < 4; i++) {
      var yy = h * (0.15 + i * 0.22);
      ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(w, yy + 10); ctx.stroke();
    }
    var tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.encoding = THREE.sRGBEncoding;
    return tex;
  }

  var pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  var envRT = pmrem.fromEquirectangular(buildEnvTexture());
  scene.environment = envRT.texture;

  // brushed-metal bump texture (procedural fine noise streaks)
  // 2026-09-23 — "add this effect on metal in low opacity on brushed
  // silver" (reference: a real brushed-aluminium sheet photo — fine,
  // continuous horizontal grain, not a dusting of random specks). The
  // previous buildBrushedBump() below randomised every pixel independently
  // (150-240 grey, no direction to it at all), which is why "Brushed" read
  // as merely duller/grainier than Polished rather than actually brushed —
  // there was no real anisotropic grain, just noise. Replaced with long,
  // thin, mostly-horizontal strokes (a few hundred, spanning most of the
  // canvas width with only slight waviness) at low per-stroke opacity
  // (0.08-0.12), so the grain itself reads as fine and continuous rather
  // than sandpaper.
  //
  // Getting this to actually SHOW UP on the model took a second fix,
  // separate from the grain design above: ExtrudeGeometry's cap-face UVs
  // are the shape's own raw coordinates (a letter spans roughly ±10-15
  // units), not normalised 0-1. The old `repeat.set(6, 1)` (and my first
  // pass at this, still at roughly that order) meant the texture repeated
  // *dozens* of times across a single letter face — far denser than one
  // screen pixel's worth of texture detail, so mipmapping quietly
  // averaged it all down to a near-flat grey before bumpScale ever got a
  // chance to matter. That's the real reason "Brushed" never looked
  // brushed, at any bumpScale. `repeat.set(0.15, 0.4)` below keeps the
  // pattern to a handful of repeats across a letter instead of dozens —
  // confirmed by rendering, not guessed: bumpScale alone (tried up to 2.0)
  // stayed invisible until repeat was fixed, and became clearly visible,
  // fine, and low-contrast the moment it was.
  function buildBrushedGrain() {
    var w = 512, h = 512;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d');
    ctx.fillStyle = 'rgb(190,190,190)';
    ctx.fillRect(0, 0, w, h);
    var strokes = 900;
    for (var i = 0; i < strokes; i++) {
      var y = Math.random() * h;
      var v = Math.round(160 + Math.random() * 55);
      var wob = (Math.random() - 0.5) * 3; // slight natural waviness, not a ruler-straight line
      ctx.strokeStyle = 'rgba(' + v + ',' + v + ',' + v + ',' + (0.08 + Math.random() * 0.04) + ')';
      ctx.lineWidth = 0.5 + Math.random() * 0.9;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y + wob);
      ctx.stroke();
    }
    var tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(0.15, 0.4);
    return tex;
  }
  var brushedBump = buildBrushedGrain();

  // ---------------------------------------------------------------
  // Floor (Technical view only) + crop-mark grid look
  // ---------------------------------------------------------------
  var floorGeo = new THREE.PlaneGeometry(600, 600);
  var floorMat = new THREE.ShadowMaterial({ opacity: 0.35 });
  var floor = new THREE.Mesh(floorGeo, floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = -26;
  floor.receiveShadow = true;
  scene.add(floor);

  // ---------------------------------------------------------------
  // Chain + bail — this geometry was only ever positioned correctly for
  // the old close-up "Studio Spotlight" camera. In the single full-plate
  // view (after that mode was removed) it renders as a cluster of rings
  // floating well above the letters, not attached to anything — the user
  // flagged this directly ("this has to go"). Left the chain/bail meshes
  // defined (harmless, unused) rather than deleting them, in case a
  // properly-attached version is worth building later; just kept out of
  // the scene. Chain type/length remain fully selectable and priced —
  // this only concerns the 3D render, same as the 3 construction styles
  // that also don't have real geometry yet.
  // ---------------------------------------------------------------
  var chainGroup = new THREE.Group();
  var chainMat = new THREE.MeshStandardMaterial({ color: 0xcfd2d3, metalness: 1, roughness: 0.3 });
  var bailGeo = new THREE.TorusGeometry(6.4, 1.15, 10, 24);
  var bail = new THREE.Mesh(bailGeo, chainMat);
  bail.rotation.x = Math.PI / 2;
  chainGroup.add(bail);
  for (var li = 0; li < 5; li++) {
    var linkGeo = new THREE.TorusGeometry(3.4, 0.85, 8, 16);
    var link = new THREE.Mesh(linkGeo, chainMat);
    link.position.y = 9 + li * 6.4;
    link.rotation.x = (li % 2 === 0) ? Math.PI / 2 : 0;
    chainGroup.add(link);
  }
  chainGroup.visible = false;
  scene.add(chainGroup);

  // ---------------------------------------------------------------
  // Nameplate geometry
  // ---------------------------------------------------------------
  var plateGroup = new THREE.Group();
  scene.add(plateGroup);
  var lastWidth = 0;
  var lastBottomY = -20;

  function countLetters(raw) {
    return raw.replace(/\s/g, '').length;
  }

  // 2026-09-24 — "make polished a tiny bit lighter (front)": small bump
  // to envMapIntensity, not just base colour — at this roughness (0.10,
  // near-mirror) the surface is mostly reflecting the scene's env map,
  // so that's what actually controls how bright it reads (same reason
  // colour alone barely moved things in the earlier engraving fixes).
  // Colour nudged up slightly too, kept deliberately small per "tiny".
  function makeMaterial(finishKey) {
    if (finishKey === 'polished') {
      return new THREE.MeshPhysicalMaterial({
        color: 0xf5f3ee, metalness: 1.0, roughness: 0.10,
        clearcoat: 0.35, clearcoatRoughness: 0.12, envMapIntensity: 1.48
      });
    }
    // 2026-09-24 — "brushed is too bright": envMapIntensity brought down
    // to match the same tuning applied to Engraved Plate's flat brushed
    // surface, so Connected Letters/Rail read the same brightness as
    // Engraved Plate instead of drifting apart. Polished is untouched.
    return new THREE.MeshPhysicalMaterial({
      color: 0xb7bab8, metalness: 1.0, roughness: 0.55,
      bumpMap: brushedBump, bumpScale: 0.22,
      envMapIntensity: 0.62
    });
  }

  function clearPlate() {
    while (plateGroup.children.length) {
      var m = plateGroup.children.pop();
      if (m.geometry) m.geometry.dispose();
      // The Engraved Plate build (see buildEngravedPlate below) gives some
      // meshes an ARRAY of two materials (mirror cap + matte cut surface),
      // the same pattern the standalone Engraved Plate R&D artifact used —
      // a plain `m.material.dispose()` throws on an array and, uncaught,
      // silently aborted every rebuild after the first there too. Dispose
      // each material individually regardless of shape.
      var mats = Array.isArray(m.material) ? m.material : (m.material ? [m.material] : []);
      for (var mi = 0; mi < mats.length; mi++) mats[mi].dispose();
      plateGroup.remove(m);
    }
  }

  // ---------------------------------------------------------------
  // Engraved Plate construction — ported from the standalone R&D
  // artifact (https://claude.ai/artifact/MF9VYhX3d3QinvAmZEzcTw, Version 7,
  // the finalized build after 3 rounds of user-caught fixes: material-group
  // split so cut surfaces read matte and only flat faces stay mirror-
  // polished; closed-counter letters (A/B/D/O/P/Q/R…) rebuilt as real
  // raised islands instead of sinking with the stroke; and a single-mesh
  // "rim" that owns the plate's entire true outer edge, replacing an
  // earlier per-slab offset hack that didn't actually fix the visible seam
  // it was meant to fix). One continuous silver piece — face slab (letters
  // + 2 mount holes cut through) glued to a floor slab underneath, both
  // inset inside a rim that alone forms the plate's real perimeter, so any
  // seam between the face/floor sits behind the rim's own wall instead of
  // on the visible silhouette.
  // ---------------------------------------------------------------
  var ENGR_PAD_X = 3.2, ENGR_PAD_BOTTOM = 3, ENGR_PAD_TOP = 6.5;
  var ENGR_HOLE_RADIUS = 1.5;
  var ENGR_HOLE_MARGIN_X = 3.6;
  var ENGR_HOLE_MARGIN_TOP = 3;
  var ENGR_FRAME_WIDTH = 1.0;

  // Same base silver as makeMaterial('polished')/('brushed'); split by
  // finish AND by cut-vs-flat so every cut surface (recess floor, letter
  // walls, mount-hole walls, the rim's own wall) reads duller than the
  // flat mirror-polished caps — see the R&D artifact's silverMat() note
  // for why (a mirror finish on the letter-hole walls fragmented the
  // letters under the studio env map, and a mirror outer edge glued to a
  // matte one looked like two different pieces of metal).
  // 2026-09-23 — "when an alphabet is engraved, it's inner should be
  // dark": the flat cap branch below (cut === false) is untouched, byte
  // for byte, from what's live today — same as makeMaterial('polished')
  // above. Only the cut (true) branch changed.
  //
  // Found by rendering, not guessed: a same-colour-but-duller cut surface
  // (the old roughness 0.55 / envMapIntensity 1.0) does NOT read as dark.
  // This scene's environment map (buildEnvTexture below) is a near-black
  // studio backdrop with only a few small bright highlight blobs, so a
  // HIGHER-roughness metal surface actually samples a wider, blurrier
  // patch of that map and picks up MORE of the bright spots — it reads
  // lighter, not darker, exactly backwards from what "duller = darker"
  // suggests. Confirmed by comparing renders: the previous cut surface
  // came out brighter than the flat cap it's supposed to sit inside.
  // Fixed by moving roughness DOWN instead of up (closer to the flat
  // cap's own near-mirror finish, so it mostly reflects the dark
  // backdrop rather than smearing in the bright spots) and pairing it
  // with a lower envMapIntensity and a distinctly darker base colour.
  // 2026-09-24 — "brushed is too bright, and still inner side of
  // alphabets are not dark": brushed's cut branch was never touched in
  // the 2026-09-23 fix above (only polished was) and had the exact same
  // "duller = darker" problem — roughness 0.7 vs the flat cap's 0.5,
  // which (same env-map reasoning as polished) reads LIGHTER, not
  // darker. Fixed the same way: cut roughness/envMapIntensity moved
  // down, colour darkened. Separately, the flat (non-cut) brushed
  // surface's envMapIntensity was also turned down a touch — reported
  // as overexposed/too bright on the live plate — independent of the
  // engraving fix and doesn't touch Polished at all.
  function engravedMat(finishKey, cut) {
    if (finishKey === 'brushed') {
      if (cut) {
        return new THREE.MeshPhysicalMaterial({
          color: 0x6d706d, metalness: 1.0, roughness: 0.4,
          bumpMap: brushedBump, bumpScale: 0.22,
          envMapIntensity: 0.4
        });
      }
      return new THREE.MeshPhysicalMaterial({
        color: 0xb7bab8, metalness: 1.0, roughness: 0.55,
        bumpMap: brushedBump, bumpScale: 0.22,
        envMapIntensity: 0.62
      });
    }
    if (cut) {
      return new THREE.MeshPhysicalMaterial({
        color: 0xb9b9b4, metalness: 1.0, roughness: 0.38,
        clearcoat: 0, clearcoatRoughness: 0.12, envMapIntensity: 0.6
      });
    }
    // 2026-09-24 — same tiny lighten as makeMaterial('polished') above,
    // kept identical between the two so Engraved Plate's flat cap and
    // Connected Letters/Rail still match each other exactly.
    return new THREE.MeshPhysicalMaterial({
      color: 0xf5f3ee, metalness: 1.0, roughness: 0.10,
      clearcoat: 0.35, clearcoatRoughness: 0.12, envMapIntensity: 1.48
    });
  }

  // ---------------------------------------------------------------
  // Hole-overlap safety net for Engraved Plate (2026-09-21 fix — "IA is
  // missing from SOPHIA, i think it's rendering problem in this variant").
  //
  // Root cause: buildEngravedPlate punches every letter's outline into the
  // face slab as a hole in ONE shared THREE.Shape (plateShape.holes). That
  // hole-punch triangulation (three.js's earcut-based ShapeUtils) assumes
  // holes don't overlap each other. font.generateShapes() lays text out
  // using the font's own baked advance widths — fine for most fonts (no
  // overlap at all), but a cursive/script font like Italianno is DESIGNED
  // with swash glyphs that overhang into the next letter's space even at
  // default spacing (that's what makes cursive text look joined). Measured
  // on "SOPHIA" at Italianno's size 30: S-O/O-P/P-H overlap ~1.5-2.2 units
  // (harmless — under this triggers no changes below) but I-A overlaps
  // 7.73 units — enough that the two holes' boundaries actually cross each
  // other, which corrupted the earcut triangulation for exactly those two
  // letters and left them engraved as a bare outline with no real recessed
  // floor (matches the reported screenshot exactly: "SOPH" cut clean,
  // "IA" a faint ghost).
  //
  // Fix: after generateShapes() lays the string out normally, walk the
  // shapes left-to-right and cap how far any pair's ink is allowed to
  // overlap. Pairs already under the cap (the normal case for every font,
  // including Italianno's own connecting swashes) are left completely
  // untouched — original bezier curves, zero visual change. A pair that
  // exceeds the cap gets nudged apart just enough to be safe, and every
  // later letter in the string carries that same shift forward so nothing
  // re-collides further down the string. Nudged shapes are rebuilt as
  // sampled polygons (translation isn't directly available on THREE.Curve
  // without reaching into curve-type-specific fields), which is what the
  // hole-punch triangulates from anyway.
  //
  // 2026-09-22 update — "overlap" is measured via maxScanlineOverlap()
  // below (a height-aware, per-scanline check), not a single global
  // bounding-box range. See the comment above capGlyphHoleOverlap() for
  // why: a bbox-range check missed a genuine crossing in Cinzel's "M"+"A"
  // (the "MA in engraved plate" report) because each shape's widest and
  // narrowest points sat at completely different heights on the letters.
  var ENGR_HOLE_SAMPLE_SEGMENTS = 28;

  function shapePointsXY(shape, segments) {
    return shape.getPoints(segments);
  }

  // 2026-09-22 fix — "MA in engraved plate" (Cinzel): the M rendered with
  // its right leg eaten away. shapeXRange() above (still used elsewhere)
  // only compares each shape's single widest/narrowest point, wherever on
  // the letter that happens to sit — for "M"+"A" those points are nowhere
  // near each other (M's widest point is up at the top of its right leg,
  // y=14.28; A's narrowest point is down at its bottom-left foot, y=0), so
  // it reported a small, harmless-looking 0.76-unit "overlap" that was
  // really just bounding-box noise. The ACTUAL danger was invisible to
  // that measure: M's serif foot and A's serif foot cross each other for
  // real, right at the baseline (boundary-to-boundary distance ~0.001
  // units there — confirmed via a direct point-in-polygon check, plotting
  // the raw glyph paths). That's what corrupted earcut's hole-bridging for
  // M, even though the SOPH/Italianno fix's original 0.76-per-size-unit
  // cap never triggered (0.76 sat comfortably under it).
  //
  // Fix: measure overlap the way it actually matters for triangulation —
  // sweep scanlines through the two shapes' shared height band and, at
  // each one, take prevShape's true rightmost ink edge minus currShape's
  // true leftmost ink edge. The worst (max) scanline is where the two
  // holes would first touch as they're pushed together, regardless of
  // which height that happens to be at — unlike a single global bbox
  // range, this can't be fooled by a widest point and a narrowest point
  // that never actually meet.
  var SCANLINE_SAMPLES = 48;

  function polyRightmostAt(pts, y) {
    var maxX = -Infinity;
    for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      var a = pts[j], b = pts[i];
      if ((a.y > y) !== (b.y > y)) {
        var x = a.x + (y - a.y) * (b.x - a.x) / (b.y - a.y);
        if (x > maxX) maxX = x;
      }
    }
    return maxX;
  }

  function polyLeftmostAt(pts, y) {
    var minX = Infinity;
    for (var i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      var a = pts[j], b = pts[i];
      if ((a.y > y) !== (b.y > y)) {
        var x = a.x + (y - a.y) * (b.x - a.x) / (b.y - a.y);
        if (x < minX) minX = x;
      }
    }
    return minX;
  }

  // Real, height-aware overlap between two adjacent glyph outlines. Only
  // scans the y-band the two shapes actually share — outside that band
  // neither shape has any ink to compare. Returns -Infinity if the shapes
  // share no height band at all (can't meaningfully overlap).
  function maxScanlineOverlap(prevPts, currPts) {
    var prevYs = prevPts.map(function (p) { return p.y; });
    var currYs = currPts.map(function (p) { return p.y; });
    var yLo = Math.max(Math.min.apply(null, prevYs), Math.min.apply(null, currYs));
    var yHi = Math.min(Math.max.apply(null, prevYs), Math.max.apply(null, currYs));
    if (yLo >= yHi) return -Infinity;
    var maxOv = -Infinity;
    for (var s = 0; s <= SCANLINE_SAMPLES; s++) {
      var y = yLo + (yHi - yLo) * (s / SCANLINE_SAMPLES);
      var r = polyRightmostAt(prevPts, y);
      var l = polyLeftmostAt(currPts, y);
      if (r === -Infinity || l === Infinity) continue;
      var ov = r - l;
      if (ov > maxOv) maxOv = ov;
    }
    return maxOv;
  }

  function shapeXRange(shape, segments) {
    var pts = shapePointsXY(shape, segments);
    var xs = pts.map(function (p) { return p.x; });
    return { min: Math.min.apply(null, xs), max: Math.max.apply(null, xs) };
  }

  function translateShapeBy(shape, dx, segments) {
    var outer = shapePointsXY(shape, segments).map(function (p) {
      return new THREE.Vector2(p.x + dx, p.y);
    });
    var moved = new THREE.Shape(outer);
    if (shape.holes && shape.holes.length) {
      moved.holes = shape.holes.map(function (h) {
        var hp = shapePointsXY(h, segments).map(function (p) {
          return new THREE.Vector2(p.x + dx, p.y);
        });
        return new THREE.Path(hp);
      });
    }
    return moved;
  }

  // Re-measured 2026-09-22 with the scanline method above instead of the
  // old global-bbox-range one. Re-running it against every font/pair this
  // project has ever had reported as broken (Italianno's SOPHIA/MDCLXVI/
  // ENZO cursive overlaps, all deliberately generous — up to several
  // units at their tightest scanline — and Cinzel's M-A, whose *only*
  // real danger was a 0.76-unit scanline overlap right at the baseline)
  // shows the two cases need genuinely different tolerances, not the same
  // dial: Italianno's connecting swashes routinely cross well past 1 unit
  // as part of the font's own design and render perfectly safely, while
  // Cinzel's M broke at just 0.76. There's no single "safe" cutoff shared
  // by both, so the honest fix is to stop treating "some overlap" as
  // basically-fine-up-to-a-big-number: this cap now only exists to catch
  // pairs that would otherwise cross by more than a hairline.
  //
  // The cap is a small NEGATIVE number — i.e. it enforces a tiny minimum
  // GAP, not just "no more than a hairline of overlap" — because a single
  // pair passing this check isn't enough. "MAMA" (Cinzel) still broke on
  // its middle "M" at a small positive cap: capped independently, each of
  // M's two neighbouring "A"s individually looked safe, but both A's
  // still converged on the exact same fragile spot on M (its baseline
  // V-notch) from opposite sides at once, which is what actually
  // corrupted the triangulation — a danger a per-pair check can't see
  // since it only ever looks at one neighbour at a time. A small enforced
  // gap on every pair, not just an upper bound on overlap, is what
  // guarantees that danger zone stays clear regardless of how many
  // neighbours are pressing on it. Confirmed by sweeping the cap down
  // pair-by-pair on "MAMA" until the middle M's ink stopped overlapping
  // either neighbour's ink at all (see capGlyphHoleOverlap's regression
  // notes in README-setup.md) — the gap only needed to be small, not
  // large, to fully clear it.
  //
  // Verified with a 9-case sweep across all 6 fonts (SOPHIA/MDCLXVI/ENZO
  // on Italianno, MAXIMA and the original MA/MAMA/MAMAMAMA cases on
  // Cinzel), a 13-case upper-vs-lowercase sweep (Engraved Plate allows
  // mixed case, unlike Connected Letters/Floating+Rail — see
  // applyCaseLock's comment), and an 11-case four-times-sandwiched sweep
  // ("amamama"/"AMAMAMA"-style strings on every font) — all clean, zero
  // JS errors, screenshot-checked — plus the full 22/22 smoke suite.
  function capGlyphHoleOverlap(shapes, fontDef) {
    var maxOverlap = fontDef.size * -0.005; // a small enforced minimum gap, not an overlap allowance — see note above
    var cumulativeShift = 0;
    var prevPts = null;
    for (var i = 0; i < shapes.length; i++) {
      if (cumulativeShift > 0) {
        shapes[i] = translateShapeBy(shapes[i], cumulativeShift, ENGR_HOLE_SAMPLE_SEGMENTS);
      }
      var pts = shapePointsXY(shapes[i], ENGR_HOLE_SAMPLE_SEGMENTS);
      if (prevPts) {
        var overlap = maxScanlineOverlap(prevPts, pts);
        if (overlap > maxOverlap) {
          var extra = overlap - maxOverlap;
          shapes[i] = translateShapeBy(shapes[i], extra, ENGR_HOLE_SAMPLE_SEGMENTS);
          pts = pts.map(function (p) { return { x: p.x + extra, y: p.y }; });
          cumulativeShift += extra;
        }
      }
      prevPts = pts;
    }
    return shapes;
  }

  // Builds directly into plateGroup (caller must have run clearPlate()
  // first) and returns the plate's own footprint so rebuildPlate() can
  // recenter the group and fit the camera the same way it does for the
  // Floating + Rail build.
  function buildEngravedPlate(text, font, fontDef, thicknessDef, finishKey) {
    // Glyph shapes are generated ONCE here (not a separate TextGeometry
    // measurement pass plus a second generateShapes() call further down for
    // the face slab, which is what this used to be) so the plate's
    // measured size and the actual hole positions can never drift apart —
    // both now come from the exact same, overlap-capped shape list. See
    // capGlyphHoleOverlap()'s comment above for why the cap exists.
    var glyphShapes = capGlyphHoleOverlap(stripZeroDotsFromString(font.generateShapes(text, fontDef.size), text, font, fontDef), fontDef);
    var gAllPts = [];
    for (var gpi = 0; gpi < glyphShapes.length; gpi++) {
      gAllPts = gAllPts.concat(shapePointsXY(glyphShapes[gpi], ENGR_HOLE_SAMPLE_SEGMENTS));
    }
    var finite = gAllPts.length > 0;
    var gXs = finite ? gAllPts.map(function (p) { return p.x; }) : [];
    var gYs = finite ? gAllPts.map(function (p) { return p.y; }) : [];
    var bbMinX = finite ? Math.min.apply(null, gXs) : 0;
    var bbMaxX = finite ? Math.max.apply(null, gXs) : 0;
    var bbMinY = finite ? Math.min.apply(null, gYs) : -fontDef.size * 0.35;
    var bbMaxY = finite ? Math.max.apply(null, gYs) : fontDef.size * 0.35;
    var mcx = finite ? (bbMinX + bbMaxX) / 2 : 0;
    var mcy = finite ? (bbMinY + bbMaxY) / 2 : 0;
    // 2026-09-22 fix — blank/empty text (which `rebuildPlate()` falls back
    // to a single space for) has no glyph, so `finite` is false here and
    // `mw` used to fall back to 0. With no padding-only plate that narrow
    // (plateW = 0 + ENGR_PAD_X*2 = 6.4), the two mount holes — positioned
    // at ±(plateW/2 - ENGR_HOLE_MARGIN_X) — landed only ~0.8 units apart
    // while each is 3 units across, so they overlapped into one merged
    // blob. This is now the FIRST thing every customer sees on Engraved
    // Plate (the "c0de93" default text was removed 2026-09-21, so the
    // field starts blank), not a rare edge case. Floating + Rail already
    // has an equivalent floor for its own blank case (`Math.max(width +
    // railPad*2, 28)` on railW) — this gives Engraved Plate the same kind
    // of safety net, sized proportionally to the font (matching how `mh`
    // already falls back to `fontDef.size * 0.7`) so the blank-state plate
    // looks like a normal-proportioned blank nameplate, roughly the size
    // an actual short name would produce, rather than a hairline sliver.
    var mw = finite ? (bbMaxX - bbMinX) : fontDef.size * 3.5;
    var mh = finite ? (bbMaxY - bbMinY) : fontDef.size * 0.7;

    var plateDepth = displayThicknessMm(thicknessDef) * UNITS_PER_MM;
    var plateW = mw + ENGR_PAD_X * 2;
    var plateH = mh + ENGR_PAD_TOP + ENGR_PAD_BOTTOM;
    var plateCy = mcy + (ENGR_PAD_TOP - ENGR_PAD_BOTTOM) / 2;
    var engraveDepth = plateDepth * 0.58;
    var floorDepth = plateDepth - engraveDepth;
    var faceZ = plateDepth / 2 - engraveDepth;
    var innerHalfW = plateW / 2 - ENGR_FRAME_WIDTH;
    var innerHalfH = plateH / 2 - ENGR_FRAME_WIDTH;

    function addMountHoles(shape, cx, cy) {
      var hx = plateW / 2 - ENGR_HOLE_MARGIN_X;
      var hy = cy + plateH / 2 - ENGR_HOLE_MARGIN_TOP;
      [-hx, hx].forEach(function (x) {
        var hole = new THREE.Path();
        hole.absarc(cx + x, hy, ENGR_HOLE_RADIUS, 0, Math.PI * 2, false);
        shape.holes.push(hole);
      });
    }

    // Rim — the plate's outer frame, BEHIND the face slab only (depth
    // floorDepth, from the plate's back up to faceZ — not the full
    // plateDepth). Outer rectangle with the inner rectangle (the
    // face/floor slabs' own boundary, below) cut out as a hole.
    //
    // 2026-09-24 fix — "why is there a line near the edges": the rim
    // used to run the FULL plateDepth, giving it its own front cap out
    // to the frame's inner edge, sitting right next to the face slab's
    // separate front cap. Same material or not, two adjacent mesh caps
    // meeting at a boundary render a faint seam line there — that's the
    // line. Fixed by shortening the rim so it stops at faceZ and
    // enlarging the face slab below to cover the frame too, so the
    // entire visible front is now ONE continuous surface with no seam.
    // The rim still backs the frame area and still owns that portion of
    // the outer wall for the depth behind faceZ — the face slab's own
    // outer wall (see below) now shares the exact same outer contour and
    // continues it for the depth in front of faceZ, so the silhouette
    // stays seamless too.
    var rimShape = new THREE.Shape();
    rimShape.moveTo(-plateW / 2, plateCy - plateH / 2);
    rimShape.lineTo(plateW / 2, plateCy - plateH / 2);
    rimShape.lineTo(plateW / 2, plateCy + plateH / 2);
    rimShape.lineTo(-plateW / 2, plateCy + plateH / 2);
    rimShape.closePath();
    var rimHole = new THREE.Path();
    rimHole.moveTo(-innerHalfW, plateCy - innerHalfH);
    rimHole.lineTo(innerHalfW, plateCy - innerHalfH);
    rimHole.lineTo(innerHalfW, plateCy + innerHalfH);
    rimHole.lineTo(-innerHalfW, plateCy + innerHalfH);
    rimHole.closePath();
    rimShape.holes.push(rimHole);
    var rimGeo = new THREE.ExtrudeGeometry(rimShape, { depth: floorDepth, bevelEnabled: false, curveSegments: 14 });
    var rimMesh = new THREE.Mesh(rimGeo, [engravedMat(finishKey, false), engravedMat(finishKey, true)]);
    rimMesh.position.set(mcx, 0, -plateDepth / 2);
    rimMesh.castShadow = true;
    rimMesh.receiveShadow = true;
    plateGroup.add(rimMesh);

    // Floor slab — inset footprint minus the two mount holes only.
    var floorShape = new THREE.Shape();
    floorShape.moveTo(-innerHalfW, plateCy - innerHalfH);
    floorShape.lineTo(innerHalfW, plateCy - innerHalfH);
    floorShape.lineTo(innerHalfW, plateCy + innerHalfH);
    floorShape.lineTo(-innerHalfW, plateCy + innerHalfH);
    floorShape.closePath();
    addMountHoles(floorShape, 0, plateCy);
    var floorGeo = new THREE.ExtrudeGeometry(floorShape, { depth: floorDepth, bevelEnabled: false, curveSegments: 14 });
    var floorMesh = new THREE.Mesh(floorGeo, engravedMat(finishKey, true));
    floorMesh.position.set(mcx, 0, -plateDepth / 2);
    floorMesh.castShadow = true;
    floorMesh.receiveShadow = true;
    plateGroup.add(floorMesh);

    // Back cap — 2026-09-24 fix, "backside needs fixing, use just one
    // tone the whole back". The rim's own back face (flat cap material)
    // and the floor's own back face (cut/recess material — darker still
    // after the 2026-09-23/24 engraving fixes above) sit side by side at
    // the same z, so the reverse of the plate showed two different tones
    // tracing the FRONT engraving's outline — a real plate's back is one
    // plain surface, it doesn't telegraph what's engraved on the front.
    // Fixed by adding one uniform slab, in the flat cap's own material,
    // covering the whole footprint (mount holes still open) and sitting
    // a hair further back than the rim/floor's existing back faces so
    // it's the only surface a rear-facing camera actually sees — the
    // two-tone faces behind it are fully hidden, front view is untouched
    // since this sits behind everything else.
    // Thin — just enough to clear z-fighting with the rim/floor's own
    // back faces behind it, not a real added thickness. plateDepth at
    // the current 0.45mm display spec is ~1.4 units (UNITS_PER_MM=3.1),
    // so this stays a small fraction of that, not a visible bulge.
    var BACK_CAP_DEPTH = 0.18;
    var backShape = new THREE.Shape();
    backShape.moveTo(-plateW / 2, plateCy - plateH / 2);
    backShape.lineTo(plateW / 2, plateCy - plateH / 2);
    backShape.lineTo(plateW / 2, plateCy + plateH / 2);
    backShape.lineTo(-plateW / 2, plateCy + plateH / 2);
    backShape.closePath();
    addMountHoles(backShape, 0, plateCy);
    var backGeo = new THREE.ExtrudeGeometry(backShape, { depth: BACK_CAP_DEPTH, bevelEnabled: false, curveSegments: 14 });
    var backMesh = new THREE.Mesh(backGeo, engravedMat(finishKey, false));
    backMesh.position.set(mcx, 0, -plateDepth / 2 - BACK_CAP_DEPTH);
    backMesh.castShadow = true;
    backMesh.receiveShadow = true;
    plateGroup.add(backMesh);

    // Face slab — 2026-09-24: now the FULL plate rectangle (frame
    // included, not just the inset inner area) in the SAME local
    // coordinate space the glyph shapes come out of, with each glyph's
    // outer contour AND the two mount holes punched through as holes.
    // glyphShapes is the same overlap-capped list computed at the top of
    // this function (not regenerated here) so the plate's measured size
    // and the actual holes can never disagree. Covering the full
    // footprint (rather than stopping at the old frame inset) is what
    // makes the whole visible front one continuous surface — see the
    // rim's comment above for why that removes the seam line.
    var plateShape = new THREE.Shape();
    plateShape.moveTo(mcx - plateW / 2, plateCy - plateH / 2);
    plateShape.lineTo(mcx + plateW / 2, plateCy - plateH / 2);
    plateShape.lineTo(mcx + plateW / 2, plateCy + plateH / 2);
    plateShape.lineTo(mcx - plateW / 2, plateCy + plateH / 2);
    plateShape.closePath();
    plateShape.holes = glyphShapes.slice();
    addMountHoles(plateShape, mcx, plateCy);
    var faceGeo = new THREE.ExtrudeGeometry(plateShape, { depth: engraveDepth, bevelEnabled: false, curveSegments: 8 });
    var faceMesh = new THREE.Mesh(faceGeo, [engravedMat(finishKey, false), engravedMat(finishKey, true)]);
    faceMesh.position.z = faceZ;
    faceMesh.castShadow = true;
    faceMesh.receiveShadow = true;
    plateGroup.add(faceMesh);

    // Counter islands — A/B/D/O/P/Q/R etc. have a fully enclosed counter;
    // punched through as part of the glyph's outer-contour hole above,
    // it would sink along with the stroke. Real engraving only cuts the
    // stroke, so each counter is rebuilt as its own small solid at the
    // same depth/z as the face slab, resting on the floor slab beneath.
    for (var gi = 0; gi < glyphShapes.length; gi++) {
      var gHoles = glyphShapes[gi].holes;
      for (var hi = 0; hi < gHoles.length; hi++) {
        var plugShape = new THREE.Shape(gHoles[hi].getPoints(fontDef.curveSegments * 3));
        var plugGeo = new THREE.ExtrudeGeometry(plugShape, { depth: engraveDepth, bevelEnabled: false, curveSegments: 8 });
        var plugMesh = new THREE.Mesh(plugGeo, [engravedMat(finishKey, false), engravedMat(finishKey, true)]);
        plugMesh.position.z = faceZ;
        plugMesh.castShadow = true;
        plugMesh.receiveShadow = true;
        plateGroup.add(plugMesh);
      }
    }

    return { width: plateW, height: plateH, cy: plateCy, cx: mcx };
  }

  // ---------------------------------------------------------------
  // Connected Letters construction — ported from the standalone R&D
  // artifact (https://claude.ai/artifact/B2cYAK5kpmkBim2YoA7hfw). Same
  // solid, beveled per-letter TextGeometry as Floating + Rail (same
  // thicknessDef-driven depth/bevel), but with no connecting rail bar —
  // the letters are simply packed tight enough to touch. Letter spacing
  // is a fixed internal constant, not a customer-facing control, same
  // principle as the rail build never exposing rail height/overlap.
  // Returns the same {width, height, cx, cy} shape as buildEngravedPlate
  // so it can share that branch's recentring/chain/pricing code below.
  //
  // 2026-09-22 rewrite — replaced the whole per-font tuned-constant
  // system (a flat `connectedGap` bounding-box offset, widened per-font,
  // then walked back per-character for narrow letters like "O", then
  // walked back AGAIN per-pair for specific combinations like "IA"/"ZO")
  // with a single geometry-driven placement that needs none of that.
  //
  // Why the old system kept regressing: `connectedGap` moved letters
  // based on their TextGeometry BOUNDING BOX, not their actual ink. A
  // bounding box tells you two letters' boxes overlap by N units, not
  // that their ink is anywhere near touching — a script capital's box
  // can extend well past where its visible stroke actually ends. That
  // "bbox-gap tautology" is exactly why Italianno needed a deep -10
  // default (verified only by trial and error against specific pairs),
  // why "O" then needed protecting from that same -10 eating it alive,
  // why "I-A" and "Z-O" then needed even deeper pair-specific overrides
  // on top of THAT — and why Pirata One's "I-A"/"Q-R" pairs (this font
  // never got any of that manual tuning) still read as disconnected even
  // though their bounding boxes report a comfortable 1.8-unit overlap:
  // Pirata One's blackletter serifs leave a lot of empty box around the
  // actual stroke, so the box overlapping tells you nothing about
  // whether the ink does.
  //
  // This rewrite measures the real glyph outlines instead (the same
  // technique buildEngravedPlate's capGlyphHoleOverlap() above already
  // uses to keep engraved holes from corrupting each other) and places
  // each letter by solving directly for the offset that gives its ink a
  // small, guaranteed overlap with the previous letter's ink — wherever
  // on the letter that closest point actually is, not wherever the
  // bounding box happens to be widest. A single swallow backstop (grid
  // coverage, same idea pairwise-check.js already used to verify this
  // by hand) then nudges narrow letters like "O" back out if the fitted
  // overlap would eat most of their own ink. Both checks are pure
  // geometry — they re-derive the right spacing for every font and every
  // pair from the glyphs themselves, so there is no per-font table left
  // to fall out of date, and no "fix a pair, regress another" cycle: a
  // pair that measures safely now stays measured, whatever font or
  // string a customer types.
  var CONNECT_SAMPLE_SEGMENTS = 40;
  // Target ink-to-ink overlap once two letters are fitted together —
  // small and constant across every font (scaled by size the same way
  // the Engraved Plate hairline cap is), just enough to guarantee real,
  // visible contact after curve tessellation, never a font-specific
  // "how much should this look fused" tuning knob.
  var CONNECT_TARGET_OVERLAP_FACTOR = -0.02;
  // Swallow backstop — identical threshold to the one pairwise-check.js
  // already validated by hand against Italianno's narrow "O": once one
  // letter's ink is more than this fraction covered by its neighbour, it
  // reads as gone, not fused.
  var CONNECT_MAX_COVERAGE_FRACTION = 0.55;
  var CONNECT_COVERAGE_GRID = 12;

  function connectPointInPolygon(pt, poly) {
    var inside = false;
    for (var i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      var xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
      var intersect = ((yi > pt.y) !== (yj > pt.y)) &&
        (pt.x < (xj - xi) * (pt.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }

  // What fraction of `minePts`'s own footprint (sampled on a grid across
  // its bounding box) also falls inside `otherPts` — i.e. how much of
  // this letter's ink the neighbour is covering.
  function connectCoverageFraction(minePts, otherPts) {
    var xs = minePts.map(function (p) { return p.x; });
    var ys = minePts.map(function (p) { return p.y; });
    var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
    var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
    var total = 0, covered = 0;
    for (var xi = 0; xi < CONNECT_COVERAGE_GRID; xi++) {
      for (var yi = 0; yi < CONNECT_COVERAGE_GRID; yi++) {
        var px = minX + (maxX - minX) * (xi + 0.5) / CONNECT_COVERAGE_GRID;
        var py = minY + (maxY - minY) * (yi + 0.5) / CONNECT_COVERAGE_GRID;
        var pt = { x: px, y: py };
        if (connectPointInPolygon(pt, minePts)) {
          total++;
          if (connectPointInPolygon(pt, otherPts)) covered++;
        }
      }
    }
    return total ? covered / total : 0;
  }

  // ---------------------------------------------------------------
  // Glyph-part bridging (2026-09-22, re-enabling lowercase on Floating +
  // Rail and Connected Letters).
  //
  // Both constructions rely on every piece of the final cut being
  // physically joined — Rail via the connecting bar underneath, Connected
  // Letters via letters fused directly to each other. Most glyphs are a
  // single closed outline, so that's automatic. But `font.generateShapes()`
  // returns some characters as TWO OR MORE separate, non-touching shapes —
  // a lowercase "i" or "j" draws its dot as a completely separate closed
  // path from the stem (that's why capitals were force-locked on these two
  // constructions in the first place — see applyCaseLock's history below).
  // A survey of every character these two constructions actually allow
  // (A-Za-z0-9, no punctuation — see ALLOWED_ALPHANUMERIC/ALLOWED_CONNECTED)
  // across all 6 fonts found the same defect on more than just i/j:
  // Technical and Pirata One's "0" draws its centre dot as a shape floating
  // free inside the digit's own counter (not touching the ring at all), and
  // Cinzel's "K"/"k" draws its diagonal leg as a separate shape that
  // doesn't quite meet the stem. (Italianno's "K" is also two shapes, but
  // they already overlap by design — nothing to fix there.) On Engraved
  // Plate this was always harmless (each shape is just a recess in a solid
  // backing plate, so an unconnected recess doesn't affect anything
  // physically) — it's specifically Rail and Connected Letters, where every
  // piece has to survive the cut as one object, where a disconnected shape
  // is an orphan that falls off.
  //
  // Fix: for any character that comes back as multiple shapes, add a thin
  // connecting bridge between each extra shape and whichever
  // already-anchored geometry (the main shape's outer contour, any of its
  // holes, or an earlier bridge) sits closest to it. The bridge is a small
  // rectangle running along the two shapes' actual closest approach, with a
  // little overlap built into each end so it's a real connection once
  // extruded, not a hairline touch. Shapes that already touch (Italianno's
  // "K") are left alone — bridgeGlyphParts only adds material where a real
  // gap exists.
  var GLYPH_BRIDGE_SAMPLE_SEGMENTS = 40;

  function glyphBridgeClosestPair(ptsA, ptsB) {
    var minD = Infinity, bestA = null, bestB = null;
    for (var i = 0; i < ptsA.length; i++) {
      for (var j = 0; j < ptsB.length; j++) {
        var d = Math.hypot(ptsA[i].x - ptsB[j].x, ptsA[i].y - ptsB[j].y);
        if (d < minD) { minD = d; bestA = ptsA[i]; bestB = ptsB[j]; }
      }
    }
    return { a: bestA, b: bestB, dist: minD };
  }

  function makeGlyphBridgeShape(pA, pB, width) {
    var dx = pB.x - pA.x, dy = pB.y - pA.y;
    var len = Math.hypot(dx, dy) || 1;
    var ux = dx / len, uy = dy / len;
    var px = -uy, py = ux;
    var hw = width / 2;
    // Extend a bit past each endpoint so the bridge genuinely overlaps
    // into both shapes once combined, rather than just kissing them at a
    // single point (which earcut/ExtrudeGeometry can't be trusted to
    // treat as a real connection — the same lesson as the letter-overlap
    // fixes above).
    var ext = width * 0.6;
    var ax = pA.x - ux * ext, ay = pA.y - uy * ext;
    var bx = pB.x + ux * ext, by = pB.y + uy * ext;
    var shape = new THREE.Shape();
    shape.moveTo(ax + px * hw, ay + py * hw);
    shape.lineTo(bx + px * hw, by + py * hw);
    shape.lineTo(bx - px * hw, by - py * hw);
    shape.lineTo(ax - px * hw, ay - py * hw);
    shape.closePath();
    return shape;
  }

  // shapes: every top-level THREE.Shape font.generateShapes() returned for
  // ONE character, already positioned in whatever coordinate space they'll
  // actually be extruded in. Returns shapes.concat(anyNeededBridges).
  function bridgeGlyphParts(shapes, fontDef) {
    if (shapes.length <= 1) return shapes;
    var bridgeWidth = fontDef.size * 0.05;
    var skipIfCloserThan = fontDef.size * 0.02; // already touching (e.g. Italianno's "K") — leave it alone
    // Anchor set starts with shape 0's own outer contour plus any of its
    // holes (a floating dot inside "0"'s counter needs to reach the
    // ring's INNER wall, not its far-away outer edge).
    var anchors = [shapes[0].getPoints(GLYPH_BRIDGE_SAMPLE_SEGMENTS)];
    if (shapes[0].holes) {
      shapes[0].holes.forEach(function (h) { anchors.push(h.getPoints(GLYPH_BRIDGE_SAMPLE_SEGMENTS)); });
    }
    var bridges = [];
    for (var i = 1; i < shapes.length; i++) {
      var pts = shapes[i].getPoints(GLYPH_BRIDGE_SAMPLE_SEGMENTS);
      var best = null;
      for (var a = 0; a < anchors.length; a++) {
        var pair = glyphBridgeClosestPair(anchors[a], pts);
        if (!best || pair.dist < best.dist) best = pair;
      }
      if (best.dist > skipIfCloserThan) {
        bridges.push(makeGlyphBridgeShape(best.a, best.b, bridgeWidth));
      }
      anchors.push(pts);
    }
    return shapes.concat(bridges);
  }

  // Cinzel's "K"/"k" foot — a shaped wedge instead of a bridge rod
  // (2026-09-22 — "Fix cinzel K, it doesn't need extra connector").
  //
  // Cinzel's "K"/"k" is the one character that still goes through the
  // generic bridgeGlyphParts() above (i/j got applyIJDotDesign, "0" got
  // stripZeroDot — see the survey comment before GLYPH_BRIDGE_SAMPLE_SEGMENTS).
  // Its diagonal leg's closest approach to the stem lands down near the
  // foot serifs (measured ~2.5-3 units apart at font size 20), so
  // bridgeGlyphParts' default hairline rod there reads as an obviously
  // bolted-on tab crossing open space — worse on "k" than "K", where a
  // first attempt at simply widening that rod also showed visible bevel
  // seams (a flat quad crossing the gap at a shallow angle relative to
  // the surrounding faces catches the light as a seam, not a blend).
  //
  // Fixed instead with a shape that matches how the gap actually reads:
  // both strokes already share the baseline (each has its own foot corner
  // sitting at y=0), and the true closest approach between the two
  // contours is a single point higher up, near where the leg's stroke
  // first swings in toward the stem. Connecting those three points — the
  // stem's own foot corner, the leg's own foot corner, and that closest
  // point — as a solid wedge reads as the two feet flaring together and
  // tapering shut, the way a bold serif "K" normally resolves that
  // corner, rather than as a separate part laid across the gap.
  function glyphFootWedgeBridge(shapes, fontDef) {
    if (shapes.length !== 2) return bridgeGlyphParts(shapes, fontDef);
    var segs = 150;
    var pts0 = shapes[0].getPoints(segs);
    var pts1 = shapes[1].getPoints(segs);
    var minY0 = Math.min.apply(null, pts0.map(function (p) { return p.y; }));
    var minY1 = Math.min.apply(null, pts1.map(function (p) { return p.y; }));
    var baseEps = fontDef.size * 0.005;
    var base0 = pts0.filter(function (p) { return p.y <= minY0 + baseEps; });
    var base1 = pts1.filter(function (p) { return p.y <= minY1 + baseEps; });
    if (!base0.length || !base1.length) return bridgeGlyphParts(shapes, fontDef);
    var corner0x = Math.max.apply(null, base0.map(function (p) { return p.x; }));
    var corner1x = Math.min.apply(null, base1.map(function (p) { return p.x; }));
    var pair = glyphBridgeClosestPair(pts0, pts1);
    // Inset every vertex a little further into its own shape so the wedge
    // genuinely overlaps both, not just touches their boundary (same
    // reasoning as makeGlyphBridgeShape's "ext" above).
    var inset = fontDef.size * 0.02;
    var p0 = { x: corner0x - inset, y: minY0 };
    var p1 = { x: corner1x + inset, y: minY1 };
    var dx = pair.b.x - pair.a.x, dy = pair.b.y - pair.a.y;
    var dlen = Math.hypot(dx, dy) || 1;
    var p2 = { x: pair.b.x + (dx / dlen) * inset, y: pair.b.y + (dy / dlen) * inset };
    var wedge = new THREE.Shape();
    wedge.moveTo(p0.x, p0.y);
    wedge.lineTo(p1.x, p1.y);
    wedge.lineTo(p2.x, p2.y);
    wedge.closePath();
    return shapes.concat([wedge]);
  }

  // ---------------------------------------------------------------
  // "i"/"j" dot placement — hand-designed, not computed (2026-09-22,
  // following "no connect them directly in the center" and the reference
  // files worked out in Illustrator).
  //
  // The generic bridgeGlyphParts() fix above works for every disconnected
  // multi-shape character, but for i/j specifically two automated attempts
  // both missed: a thin rectangular bridge between the dot and stem reads
  // as an obvious separate part once you look closely (a straight-edged
  // tab that doesn't belong to the letterform), and pulling the dot along
  // the vector to its single nearest point on the stem drags it off-centre
  // toward whatever corner happened to be closest, rather than sitting
  // squarely above the stroke the way a real tittle does.
  //
  // Fixed by hand instead of by formula: the exact font files were
  // exported (font-downloads/*.ttf) and, for i/j, an SVG of the dot and
  // stem as separate paths at true font-unit scale (font-downloads/svg/
  // *-ij.svg) — re-scaled and re-centred directly on the stem in
  // Illustrator, one font at a time, then measured back (see
  // ij-transform-summary2.json in scratchpad) to get an exact
  // scale-about-own-centre + translate for each font's dot, in the same
  // font units core.js already works in. IJ_DOT_DESIGN below is that
  // table. Cinzel has no entry — its lowercase i/j already render as the
  // same solid capital-shaped glyph as I/J (no separate dot to begin
  // with), so nothing needs fixing there.
  //
  // Unlike bridgeGlyphParts (which ADDS a bridge shape alongside the
  // untouched original pieces), applyIJDotDesign REPLACES the dot shape
  // in place with a rescaled, repositioned copy of itself that now
  // genuinely overlaps the stem — same array length in and out, no
  // separate connector piece, and because it's a scaled copy of the
  // font's own dot (not a hand-redrawn shape), every font keeps its own
  // dot's natural roundness/shape, just resized and moved.
  // 2026-09-23 update: zillaslab's dot needed a ROTATION, not just
  // scale+translate -- its native i/j dot glyph is a plain axis-aligned
  // square, but the brand reference (zillaslab-ij-v2.svg) calls for a
  // diamond (that square rotated 45°). scaleTranslateShapeToPoly below
  // now takes an optional rotationDeg (rotate about the shape's own
  // centre, before scale+translate -- order matters for a non-uniform
  // scale but rotation/translation both commute fine with the polygon
  // resampling already in use here). zillaslab's scale/dy were also
  // sized down and re-anchored per follow-up feedback ("make dots bit
  // smaller" / "a bit more smaller" / "move down for connection") --
  // verified against the reference SVG via flat-vector re-renders
  // (scratchpad/make-ij-transformed-svg.js) before landing here.
  //
  // pirataone is now per-glyph ({i:..., j:...}) instead of one value
  // averaged across both -- the "i" and "j" dot measurements
  // (ij-transform-summary2.json) differ by ~22 units of dy, too wide a
  // spread to average without leaving one of the two not touching its
  // stem (confirmed: averaged value left "i" with a visible gap while
  // "j" looked fine). applyIJDotDesign() below picks the right one by
  // character.
  var IJ_DOT_DESIGN = {
    plex:      { scaleX: 1.000, scaleY: 1.000, dx: -90.3, dy: -76.5 },
    barlow:    { scaleX: 0.55,  scaleY: 0.55,  dx: -1.2,  dy: -97.0 },
    pirataone: {
      i: { scaleX: 1.230, scaleY: 0.898, dx: -5.1, dy: -88.9 },
      j: { scaleX: 1.230, scaleY: 0.898, dx: -8.6, dy: -66.9 }
    },
    zillaslab: { scaleX: 0.886, scaleY: 0.852, dx: -27.5, dy: -73.0, rotationDeg: 45 },
    italianno: { scaleX: 1.000, scaleY: 1.000, dx: -18.0, dy: -105.5 }
    // cinzel intentionally omitted -- see note above.
  };
  var IJ_DOT_SAMPLE_SEGMENTS = 40;

  // Same resample-to-polygon technique as translateShapeToPoly-style
  // fixes elsewhere in this file (THREE.Curve has no generic
  // transform, see the comment above capGlyphHoleOverlap) — rotates a
  // shape about its own bounding-box centre (if rotationDeg given),
  // then scales it about that same centre, then translates it.
  function scaleTranslateShapeToPoly(shape, scaleX, scaleY, dx, dy, segments, rotationDeg) {
    var pts = shape.getPoints(segments);
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    pts.forEach(function (p) {
      if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
    });
    var cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    var rad = (rotationDeg || 0) * Math.PI / 180;
    var cos = Math.cos(rad), sin = Math.sin(rad);
    var ns = new THREE.Shape();
    pts.forEach(function (p, i) {
      var rx = p.x - cx, ry = p.y - cy;
      if (rotationDeg) {
        var rrx = rx * cos - ry * sin;
        var rry = rx * sin + ry * cos;
        rx = rrx; ry = rry;
      }
      var x = cx + rx * scaleX + dx;
      var y = cy + ry * scaleY + dy;
      if (i === 0) ns.moveTo(x, y); else ns.lineTo(x, y);
    });
    ns.closePath();
    return ns;
  }

  // shapes: every top-level shape font.generateShapes() returned for one
  // "i" or "j" character (dot + stem, in whatever order the font data
  // happens to return them). Identifies the dot as the smaller-bbox-area
  // shape (true across every font surveyed) and replaces it with the
  // IJ_DOT_DESIGN-transformed version. Returns shapes unchanged if this
  // font has no design entry (Cinzel) or the character wasn't actually
  // multi-shape.
  function applyIJDotDesign(shapes, fontDef, ch) {
    var design = IJ_DOT_DESIGN[fontDef.key];
    if (!design || shapes.length <= 1) return shapes;
    // Per-glyph override (currently just pirataone, whose "i"/"j" dots
    // need different placement -- see note above IJ_DOT_DESIGN). Falls
    // back to the flat per-font design object for every other font.
    if (design.i || design.j) design = design[ch] || design.i || design.j;
    var boxes = shapes.map(function (s) {
      var pts = s.getPoints(IJ_DOT_SAMPLE_SEGMENTS);
      var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      pts.forEach(function (p) {
        if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
      });
      return (maxX - minX) * (maxY - minY);
    });
    var dotIdx = 0;
    for (var i = 1; i < boxes.length; i++) { if (boxes[i] < boxes[dotIdx]) dotIdx = i; }
    // IJ_DOT_DESIGN's dx/dy were measured against the RAW glyph outline
    // data (the font's native 1000-unit em-square -- same space as
    // x_min/x_max in the font JSON), but font.generateShapes(ch, size)
    // already scales every point down by size/resolution before shapes
    // ever reach here. Applying the raw-unit deltas directly overshoots
    // by exactly that factor (~48x at Plex's size 21) and sends the dot
    // nowhere near the letter -- convert dx/dy into the same scaled
    // space the shape is actually in before translating. scaleX/scaleY
    // are unitless ratios and need no conversion.
    var unitsScale = fontDef.size / (fontDef.data.resolution || 1000);
    var out = shapes.slice();
    out[dotIdx] = scaleTranslateShapeToPoly(
      shapes[dotIdx], design.scaleX, design.scaleY,
      design.dx * unitsScale, design.dy * unitsScale, IJ_DOT_SAMPLE_SEGMENTS,
      design.rotationDeg
    );
    return out;
  }

  // ---------------------------------------------------------------
  // "0" centre dot removal (2026-09-22 — "remove dots from 0, don't want
  // them, keep them simple"). IBM Plex Mono (Technical) and Pirata One
  // both draw the digit "0" with a small extra dot floating in the
  // counter (a "dotted zero", the same style typewriters/terminals use to
  // tell 0 apart from O) — a separate shape from the ring, same defect
  // family as the i/j dot. Rather than bridge or reposition it like i/j,
  // the brief here is simpler: just don't render it. Every other font's
  // "0" is already a single ring shape with a plain hole, so this is a
  // no-op there.
  function smallestBBoxShapeIndex(shapes, segments) {
    if (!shapes.length) return -1;
    var areas = shapes.map(function (s) {
      var pts = s.getPoints(segments);
      var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      pts.forEach(function (p) {
        if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
      });
      return (maxX - minX) * (maxY - minY);
    });
    var idx = 0;
    for (var i = 1; i < areas.length; i++) { if (areas[i] < areas[idx]) idx = i; }
    return idx;
  }

  // shapes: every top-level shape for one isolated "0" character. Drops
  // the smaller-bbox shape (the floating dot) if there is one; returns
  // shapes untouched for a font whose "0" is already a single shape.
  function stripZeroDot(shapes, segments) {
    if (shapes.length <= 1) return shapes;
    var dotIdx = smallestBBoxShapeIndex(shapes, segments || IJ_DOT_SAMPLE_SEGMENTS);
    return shapes.filter(function (_, i) { return i !== dotIdx; });
  }

  // Same idea as stripZeroDot, but for a shapes array already laid out for
  // a WHOLE string (Engraved Plate builds one shared shape list for the
  // full string rather than per-character) -- walks the text to find each
  // "0"'s own slice of the array and drops its dot from within that
  // slice, by absolute index, in one pass at the end.
  function stripZeroDotsFromString(shapes, text, font, fontDef) {
    var cursor = 0;
    var removeSet = null;
    text.split('').forEach(function (ch) {
      if (!fontDef.data.glyphs || !fontDef.data.glyphs[ch]) return;
      var count = font.generateShapes(ch, fontDef.size).length;
      if (ch === '0' && count > 1) {
        var group = shapes.slice(cursor, cursor + count);
        var dotIdx = smallestBBoxShapeIndex(group, ENGR_HOLE_SAMPLE_SEGMENTS);
        if (dotIdx !== -1) {
          removeSet = removeSet || {};
          removeSet[cursor + dotIdx] = true;
        }
      }
      cursor += count;
    });
    if (!removeSet) return shapes;
    return shapes.filter(function (_, idx) { return !removeSet[idx]; });
  }

  function buildConnectedLetters(text, font, fontDef, thicknessDef, finishKey) {
    var plateDepth = displayThicknessMm(thicknessDef) * UNITS_PER_MM;
    var bevelThickness = plateDepth * 0.15;
    var mat = makeMaterial(finishKey);
    var targetOverlap = fontDef.size * CONNECT_TARGET_OVERLAP_FACTOR;

    var letters = []; // {ch, minX, maxX, minY, maxY, pts (absolute-position raw ink samples)}
    var chars = text.split('');

    for (var i = 0; i < chars.length; i++) {
      var ch = chars[i];
      if (!fontDef.data.glyphs || !fontDef.data.glyphs[ch]) continue;

      // Real ink outline for this glyph, in its own untranslated local
      // coordinates — the same source buildEngravedPlate's overlap check
      // uses, so placement is driven by the actual shape, not a box. Most
      // characters are a single shape, but a few (lowercase "i"/"j", "0" on
      // some fonts, Cinzel's "K"/"k") come back as more than one — union
      // every shape's points so a multi-part glyph's full ink extent is
      // what gets fitted against its neighbour, not just its first piece.
      var rawShapes = font.generateShapes(ch, fontDef.size);
      if (!rawShapes.length) continue;
      var rawPts = [];
      rawShapes.forEach(function (s) { rawPts = rawPts.concat(s.getPoints(CONNECT_SAMPLE_SEGMENTS)); });
      var finite = rawPts.length > 0 && rawPts.every(function (p) { return isFinite(p.x) && isFinite(p.y); });
      if (!finite) continue;

      var offsetX;
      if (!letters.length) {
        // First letter: no previous ink to fit against. Its absolute
        // position is arbitrary (the whole string gets recentred below),
        // so just place it at its own natural local origin.
        offsetX = 0;
      } else {
        var prevLetter = letters[letters.length - 1];
        // maxScanlineOverlap(prev, curr) is linear in curr's shift: moving
        // curr right by any amount reduces every scanline's overlap by
        // exactly that amount. So the overlap measured with curr still at
        // its own natural (unshifted) position tells us, in one step, the
        // exact offset that lands the fitted overlap on `targetOverlap` —
        // no trial-and-error search needed.
        var naturalOverlap = maxScanlineOverlap(prevLetter.pts, rawPts);
        offsetX = naturalOverlap - targetOverlap;

        // Swallow backstop — back off in small steps if this fit would
        // cover most of either letter's own ink. Overlap (and therefore
        // coverage) only decreases as offsetX grows, so this always
        // converges; capped at a generous iteration count as a safety net.
        for (var guard = 0; guard < 60; guard++) {
          var shiftedPts = rawPts.map(function (p) { return { x: p.x + offsetX, y: p.y }; });
          var covCurrByPrev = connectCoverageFraction(shiftedPts, prevLetter.pts);
          var covPrevByCurr = connectCoverageFraction(prevLetter.pts, shiftedPts);
          if (covCurrByPrev <= CONNECT_MAX_COVERAGE_FRACTION && covPrevByCurr <= CONNECT_MAX_COVERAGE_FRACTION) break;
          offsetX += fontDef.size * 0.01;
        }
      }

      var absPts = rawPts.map(function (p) { return { x: p.x + offsetX, y: p.y }; });
      var absXs = absPts.map(function (p) { return p.x; });
      var absYs = absPts.map(function (p) { return p.y; });

      // Built from the same rawShapes used for placement above (plus any
      // connecting bridge bridgeGlyphParts() adds for a multi-part glyph),
      // via ExtrudeGeometry directly rather than THREE.TextGeometry — this
      // is exactly what TextGeometry does internally (font.generateShapes()
      // then ExtrudeGeometry with these same params, confirmed identical
      // vertex-for-vertex), just with a hook to fix up the shapes first.
      var meshShapes = (ch === 'i' || ch === 'j') ? applyIJDotDesign(rawShapes, fontDef, ch)
        : (ch === '0') ? stripZeroDot(rawShapes, CONNECT_SAMPLE_SEGMENTS)
        : (fontDef.key === 'cinzel' && (ch === 'K' || ch === 'k')) ? rawShapes /* no connector — see 2026-09-22 "remove these extra K connectors completely" */
        : bridgeGlyphParts(rawShapes, fontDef);
      var geo = new THREE.ExtrudeGeometry(meshShapes, {
        depth: plateDepth, steps: 1,
        bevelEnabled: true, bevelThickness: bevelThickness, bevelSize: fontDef.bevelSize,
        curveSegments: fontDef.curveSegments
      });
      var mesh = new THREE.Mesh(geo, mat);
      // offsetX is computed in the same local coordinate space the raw
      // glyph shapes and this geometry share (bevel flares symmetric
      // ±bevelSize around that same base silhouette, so it never shifts
      // which offset lands two letters' ink at the target overlap).
      mesh.position.set(offsetX, 0, -plateDepth / 2);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      plateGroup.add(mesh);

      letters.push({
        ch: ch,
        minX: Math.min.apply(null, absXs), maxX: Math.max.apply(null, absXs),
        minY: Math.min.apply(null, absYs), maxY: Math.max.apply(null, absYs),
        pts: absPts
      });
    }

    if (!letters.length) {
      return { width: 0, height: fontDef.size * 0.7, cx: 0, cy: 0 };
    }

    var totalMinX = Math.min.apply(null, letters.map(function (l) { return l.minX; }));
    var totalMaxX = Math.max.apply(null, letters.map(function (l) { return l.maxX; }));
    var totalMinY = Math.min.apply(null, letters.map(function (l) { return l.minY; }));
    var totalMaxY = Math.max.apply(null, letters.map(function (l) { return l.maxY; }));

    return {
      width: totalMaxX - totalMinX,
      height: totalMaxY - totalMinY,
      cx: (totalMinX + totalMaxX) / 2,
      cy: (totalMinY + totalMaxY) / 2
    };
  }

  function rebuildPlate() {
    clearPlate();
    // Rail geometry below is translated so its own horizontal centre sits
    // at local x=0 (see geo.translate(-cx, ...) further down), so it never
    // needs plateGroup itself shifted sideways. The Engraved Plate build
    // does NOT re-center its geometry that way — it keeps the glyph
    // shapes in their own natural font-space coordinates and instead
    // shifts plateGroup.position.x (see the 'engraved' branch below) — so
    // this reset is needed here, otherwise switching From Engraved back to
    // Floating + Rail would leave the whole rail plate visibly shifted
    // sideways by whatever x-offset the last engraved rebuild set.
    plateGroup.position.x = 0;
    var raw = input.value;
    var text = raw.length ? raw : ' ';
    var fontDef = FONTS[currentFontKey];
    var font = new THREE.Font(fontDef.data);
    // Metal thickness is a true physical depth: displayThicknessMm(thicknessDef)
    // converted to scene units via UNITS_PER_MM. This replaced an earlier
    // "mult" scheme that scaled a large stylised base depth (7-8 units) and
    // rendered far thicker than an actual 0.7-1mm laser-cut sheet. (The
    // on-screen "~Xmm wide" width label that used this same conversion
    // factor was removed 2026-09-14 — see the UNITS_PER_MM comment above.)
    // bevelThickness is kept as a fixed fraction of that real depth (not a
    // literal mm figure) so the chamfer still reads as a visible edge catch
    // rather than vanishing or overwhelming the thin plate. The real spec
    // is always the single fixed 0.70mm gauge (THICKNESS.standard.mm) — the
    // geometry above renders at THICKNESS.standard.displayMm instead (see
    // the THICKNESS comment above), a render-only thinner look that never
    // touches the label or the real spec.
    var thicknessDef = THICKNESS.standard;

    // Engraved Plate and Connected Letters both have real geometry (each
    // ported from its own standalone R&D artifact) and share this branch
    // — buildEngravedPlate/buildConnectedLetters both return the same
    // {width, height, cx, cy} shape, keeping glyphs in their natural
    // font-space coordinates rather than pre-translating the geometry, so
    // the recentring/chain/pricing code below applies to either build
    // unchanged. Floating + Rail is the only construction with its own
    // separate (pre-translated) path further down.
    var constructionKey = currentConstructionKey();
    if (constructionKey === 'engraved' || constructionKey === 'connected') {
      var dims = constructionKey === 'engraved'
        ? buildEngravedPlate(text, font, fontDef, thicknessDef, currentFinishKey)
        : buildConnectedLetters(text, font, fontDef, thicknessDef, currentFinishKey);
      lastWidth = dims.width;
      var engrTotalMinY = dims.cy - dims.height / 2;
      var engrTotalMaxY = dims.cy + dims.height / 2;
      // Same recentring approach as the rail build below: shift the whole
      // group so its own midpoint sits at (0,0) — buildEngravedPlate keeps
      // its meshes in the glyphs' natural font-space coordinates rather
      // than pre-translating the geometry itself, so BOTH axes need
      // correcting here (rail only needs Y because its own TextGeometry is
      // translated to x=0 directly, see geo.translate() below).
      plateGroup.position.x = -dims.cx;
      plateGroup.position.y = -dims.cy;
      lastBottomY = engrTotalMinY - dims.cy;
      bail.position.y = engrTotalMaxY - dims.cy + 9;
      for (var eci = 1; eci < chainGroup.children.length; eci++) {
        chainGroup.children[eci].position.y = bail.position.y + 3 + (eci - 1) * 6.4;
      }
      var engrLetters = countLetters(raw);
      letterCountEl.textContent = engrLetters;
      priceEl.textContent = (engrLetters * RATE).toFixed(2);
      cta.disabled = engrLetters === 0;
      if (specFinishLabel) specFinishLabel.textContent = FINISH[currentFinishKey].label.toUpperCase();
      fitCameraTarget(dims.width, dims.height);
      return;
    }

    var plateDepth = displayThicknessMm(thicknessDef) * UNITS_PER_MM;
    // Keep the bevel a modest slice of the (now genuinely thin) depth — a
    // bigger ratio here rounds the whole edge into a pillow/lozenge profile
    // that reads as thick even when the true depth is tiny; 15% leaves a
    // visible flat band down the middle of the edge, which is what actually
    // reads as "thin flat plate with a light chamfer" rather than "chunky".
    var bevelThickness = plateDepth * 0.15;

    // Built from font.generateShapes(text, ...) directly (what
    // THREE.TextGeometry does internally anyway — confirmed identical
    // vertex-for-vertex against the same params) instead of the
    // TextGeometry convenience call, so a character that comes back as
    // multiple disconnected shapes (lowercase "i"/"j", "0" on some fonts,
    // Cinzel's "K"/"k" — see bridgeGlyphParts() above) can get a connecting
    // bridge added before extrusion. The whole string is still laid out in
    // one generateShapes() call, so every letter keeps the font's own
    // natural spacing exactly as before — only characters that actually
    // need a bridge get anything added, everything else is untouched.
    var stringShapes = font.generateShapes(text, fontDef.size);
    var railMeshShapes = stringShapes.slice();
    var railShapeCursor = 0;
    // Absolute indices into the stringShapes-length prefix of
    // railMeshShapes to drop entirely (e.g. "0"'s centre dot on Technical/
    // Pirata One) -- collected during this same walk and removed in one
    // pass afterward, since deleting mid-walk would desync railShapeCursor
    // from the per-character counts every later character depends on.
    var railRemoveIndices = [];
    text.split('').forEach(function (ch) {
      if (!fontDef.data.glyphs || !fontDef.data.glyphs[ch]) return;
      var charShapeCount = font.generateShapes(ch, fontDef.size).length;
      if (charShapeCount > 1) {
        var group = stringShapes.slice(railShapeCursor, railShapeCursor + charShapeCount);
        if (ch === 'i' || ch === 'j') {
          // applyIJDotDesign REPLACES the dot shape in place (same length
          // in/out) rather than adding a bridge -- splice the redesigned
          // shapes back over their original slots in railMeshShapes.
          var redesigned = applyIJDotDesign(group, fontDef, ch);
          for (var gi = 0; gi < redesigned.length; gi++) {
            railMeshShapes[railShapeCursor + gi] = redesigned[gi];
          }
        } else if (ch === '0') {
          var dotIdx = smallestBBoxShapeIndex(group, GLYPH_BRIDGE_SAMPLE_SEGMENTS);
          if (dotIdx !== -1) railRemoveIndices.push(railShapeCursor + dotIdx);
        } else if (fontDef.key === 'cinzel' && (ch === 'K' || ch === 'k')) {
          // no connector — see 2026-09-22 "remove these extra K connectors
          // completely"; group is already in railMeshShapes untouched.
        } else {
          var withBridges = bridgeGlyphParts(group, fontDef);
          railMeshShapes = railMeshShapes.concat(withBridges.slice(group.length));
        }
      }
      railShapeCursor += charShapeCount;
    });
    if (railRemoveIndices.length) {
      var railRemoveSet = {};
      railRemoveIndices.forEach(function (idx) { railRemoveSet[idx] = true; });
      railMeshShapes = railMeshShapes.filter(function (_, idx) { return !railRemoveSet[idx]; });
    }

    var geo = new THREE.ExtrudeGeometry(railMeshShapes, {
      depth: plateDepth,
      steps: 1,
      curveSegments: fontDef.curveSegments,
      bevelEnabled: true,
      bevelThickness: bevelThickness,
      bevelSize: fontDef.bevelSize,
      bevelSegments: 2
    });
    geo.computeBoundingBox();
    var bb = geo.boundingBox;
    // A blank / whitespace-only string (or one made only of unsupported
    // characters) produces a zero-vertex TextGeometry whose bounding box
    // comes back as +/-Infinity. Guard against that degenerate case so it
    // can never feed NaN into the rail size or the orbit-camera distance
    // clamp (which, once NaN, corrupts the camera permanently).
    var finiteBox = isFinite(bb.min.x) && isFinite(bb.max.x) && isFinite(bb.min.y) && isFinite(bb.max.y);
    var cx = finiteBox ? (bb.min.x + bb.max.x) / 2 : 0;
    var cy = finiteBox ? (bb.min.y + bb.max.y) / 2 : 0;
    // Save the pre-translate extents as plain numbers before calling
    // geo.translate() below. BufferGeometry.translate() recomputes an
    // existing boundingBox in place, so `bb` (a live reference to
    // geo.boundingBox) silently becomes the POST-translate box the moment
    // translate() runs. Reading bb.min.y/bb.max.y after that point and
    // subtracting cy again double-shifted them — this was why the rail sat
    // roughly twice as far from the letters as intended (and the letters'
    // own vertical centering was off), producing the visible gap.
    var rawMinY = finiteBox ? bb.min.y : null;
    var rawMaxY = finiteBox ? bb.max.y : null;
    geo.translate(-cx, -cy, -plateDepth / 2);

    var width = finiteBox ? (bb.max.x - bb.min.x) : 0;
    var minY = finiteBox ? (rawMinY - cy) : -fontDef.size * 0.35;
    var maxYForBox = finiteBox ? (rawMaxY - cy) : fontDef.size * 0.35;

    var mat = makeMaterial(currentFinishKey);
    var mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    plateGroup.add(mesh);

    // connecting rail — every letter fuses to one continuous bar
    // Overhang scales with the active font's size (2026-09-21: "rail needs
    // to match the font size, just slightly longer than font") instead of
    // the old flat 3-unit pad, which was tuned by eye against Terminal
    // (size 26) alone and looked increasingly too long/short as fonts with
    // very different sizes (currently 21-26, but the render harness already
    // has candidates from 14 up to 30+) get wired in. 3 / 26 ≈ 0.115 keeps
    // Terminal's already-approved look pixel-for-pixel the same while
    // letting every other font get its own proportional "just slightly
    // longer than the letters" overhang.
    var railPad = fontDef.size * 0.115;
    // 2026-09-23 fix — "shorten this bar/rail length": the flat `28` floor
    // below was meant only as a safety net for BLANK text (so an empty
    // plate doesn't render as a hairline sliver — the finiteBox comment
    // above explains why width falls back to 0 there), the same role
    // Engraved Plate's `fontDef.size * 3.5` fallback plays for its own mw.
    // But Math.max() applied it unconditionally, so it also clamped every
    // ordinary SHORT string (a single real letter like "A" — width ~14,
    // +railPad*2 ~19 total, both well under 28) up to a fixed 28 regardless
    // of font size, which is exactly the visible overhang the live preview
    // showed: the rail reading much wider than the letter it's under. Real,
    // finite-width text now always uses its own computed width + padding,
    // uninflated; only the genuinely blank/degenerate case still gets a
    // proportional (not flat) fallback, mirroring Engraved Plate's pattern.
    var railW = finiteBox ? (width + railPad * 2) : (fontDef.size * 3.5 + railPad * 2);
    // Rail height and overlap were cut roughly in half (from 4.6/2 to
    // 2.2/1) so the rail reads as a quiet, minimal connecting bar rather
    // than a thick block competing with the letters — user: "Word and
    // rail touching has to be quiet minimal."
    var railH = 2.2;
    // The rail is cut from the same flat sheet as the letters, so it must
    // physically be the same thickness — previously this was
    // `plateDepth * 0.55`, a thinner slab than the letters, which wasn't
    // just a style choice but wrong: a real one-piece laser-cut plate
    // can't have two different thicknesses. User: "thickness of rail
    // should be the same as the name."
    var railDepth = plateDepth;
    // A plain BoxGeometry has hard, unbeveled edges — even at the exact
    // same numeric depth as the letters, its big flat side face reads as
    // visibly thicker than the letters' softly beveled profile (user:
    // "Still coming out, it has to be the same" — the mismatch was never
    // the depth *number*, which was already equal, it was that the rail
    // had no bevel to match the letters' softened edge). Built as a
    // beveled extrusion instead, using the exact same bevel params as the
    // letters, so it visually reads as the same material.
    var railShape = new THREE.Shape();
    railShape.moveTo(-railW / 2, -railH / 2);
    railShape.lineTo(railW / 2, -railH / 2);
    railShape.lineTo(railW / 2, railH / 2);
    railShape.lineTo(-railW / 2, railH / 2);
    railShape.closePath();
    var railGeo = new THREE.ExtrudeGeometry(railShape, {
      depth: railDepth,
      bevelEnabled: true,
      bevelThickness: bevelThickness,
      bevelSize: fontDef.bevelSize,
      bevelSegments: 2,
      curveSegments: 1
    });
    // ExtrudeGeometry runs z:0 -> depth by default; recenter on z:0 to
    // match the letters (which are centered the same way, see the
    // geo.translate(-cx, -cy, -plateDepth/2) call above).
    railGeo.translate(0, 0, -railDepth / 2);
    var railMesh = new THREE.Mesh(railGeo, mat);
    // Embed the rail up into the letters' bottom edge instead of leaving a
    // gap below them — this is what actually reads as "fused" (a real
    // laser-cut piece has zero seam here). Previously this was
    // `minY - 4 - railH / 2`, which left a visible 4-unit gap between the
    // letters and the rail.
    //
    // IMPORTANT (2026-09-21 fix — "rail is not connected properly with all
    // alphabets"): this used to anchor to `minY`, the bounding box of the
    // WHOLE string. That's fine for an all-caps or no-descender string
    // (every letter's bottom edge already sits on the shared baseline, so
    // the box min IS the baseline), which is exactly what every font/name
    // combo had been verified with up to now (c0de93, SOPHIA, ANASASMAT —
    // no descenders anywhere). But a real lowercase name with a descender
    // letter (p/g/y/j/q — e.g. "sophia") drags the WHOLE string's minY down
    // to the bottom of that one letter's descender, since it's a single
    // shared bounding box. The rail then sits low enough to fuse with the
    // descender but leaves every non-descender letter (s/o/h/i/a) floating
    // above it with a visible gap — only the letter that happens to have
    // the deepest descender was ever actually touching the rail.
    // The fix: anchor to the shared baseline itself (`-cy`, i.e. font-space
    // y=0 after the same -cy recentring translate applied to the glyph
    // geometry above), which is where every letter's main stroke actually
    // crosses regardless of ascenders/descenders. A descender just hangs
    // below the rail as a tail — still fused, because the stroke crossing
    // the baseline is what's embedded into the rail, not the tip of the
    // descender. For any string with no descenders this is numerically
    // identical to the old `minY`, so every previously-verified render is
    // unaffected.
    var baselineY = -cy;
    // ---------------------------------------------------------------
    // Rail-to-letter gap (2026-10-02 — "increase the distance between
    // bar and text"). This is the one number that controls it:
    //   positive railOverlap  -> rail pushes UP into the letters (fused,
    //                            little/no visible gap — this was 1)
    //   0                     -> rail just touches the letters
    //   negative railOverlap  -> rail drops DOWN, opening a real,
    //                            visible gap between rail and letters
    // Changed from 1 to -2 to open a small, clearly visible gap. Nudge
    // this number (e.g. -3, -4) for a bigger gap, or back toward 0/1 to
    // tighten it up again.
    // ---------------------------------------------------------------
    var railOverlap = 0;
    railMesh.position.y = baselineY + railOverlap - railH / 2;
    railMesh.castShadow = true;
    railMesh.receiveShadow = true;
    plateGroup.add(railMesh);

    lastWidth = railW;

    // recenter whole plate vertically so it sits nicely mid-frame. Use
    // whichever is actually lowest — the rail's own bottom edge, or a
    // descender tip hanging below it (minY, the true glyph-geometry
    // bounding box) — so camera framing and vertical centring stay correct
    // for names like "sophia" where the 'p' tail extends past the rail.
    var railBottom = railMesh.position.y - railH / 2;
    var totalMinY = Math.min(railBottom, minY);
    var totalMaxY = maxYForBox;
    var mid = (totalMinY + totalMaxY) / 2;
    plateGroup.position.y = -mid;
    lastBottomY = totalMinY - mid;

    // reposition chain/bail relative to plate top
    bail.position.y = maxYForBox - mid + 9;
    for (var i2 = 1; i2 < chainGroup.children.length; i2++) {
      chainGroup.children[i2].position.y = bail.position.y + 3 + (i2 - 1) * 6.4;
    }

    var letters = countLetters(raw);
    letterCountEl.textContent = letters;
    var price = (letters * RATE).toFixed(2);
    priceEl.textContent = price;
    // Written into its own price line (not folded into "Plate") so the
    // thickness surcharge stays a separate, itemized line the customer can
    // see — the v2 pricing script picks this up via a MutationObserver,
    // the same way it already observes the plate price.
    cta.disabled = letters === 0;
    if (specFinishLabel) specFinishLabel.textContent = FINISH[currentFinishKey].label.toUpperCase();

    fitCameraTarget(railW, (totalMaxY - totalMinY));
  }

  var desiredDistance = 190;

  // Precise perspective-camera framing: given the plate's on-screen width
  // and height, find the distance at which both dimensions fit inside the
  // frustum (with headroom), using the camera's actual fov/aspect rather
  // than a hand-tuned constant — a fixed multiplier looked right for a
  // 4-letter word and cropped anything much longer.
  function fitCameraTarget(width, height) {
    // Defensive: never let a non-finite input reach controls.min/maxDistance —
    // OrbitControls has no recovery from a NaN distance clamp once it's fed
    // into the camera's spherical position (see rebuildPlate's finiteBox guard
    // for where this could otherwise originate).
    if (!isFinite(width) || !isFinite(height)) return;
    var pad = 1.6; // headroom so the plate never touches the frame edge
    var vFov = camera.fov * Math.PI / 180;
    var aspect = camera.aspect || 1;
    var distForWidth = (width / 2 * pad) / (Math.tan(vFov / 2) * aspect);
    var distForHeight = (height / 2 * pad) / Math.tan(vFov / 2);
    var targetDist = Math.min(Math.max(Math.max(distForWidth, distForHeight), 70), 520);
    controls.minDistance = targetDist * 0.5;
    controls.maxDistance = targetDist * 2.6;
    desiredDistance = targetDist;
    cameraFitActive = true;
  }

  // Smoothly glide the camera to `desiredDistance` (set whenever the plate's
  // size changes) without fighting OrbitControls' own rotation/damping —
  // adjust the radius of the existing camera->target offset each frame,
  // then let controls.update() do its usual work on top of that.
  function approachDesiredDistance() {
    var offset = new THREE.Vector3().subVectors(camera.position, controls.target);
    var len = offset.length();
    if (!isFinite(len) || len < 1e-4) return;
    var next = len + (desiredDistance - len) * 0.08;
    offset.multiplyScalar(next / len);
    camera.position.copy(controls.target).add(offset);
  }

  // ---------------------------------------------------------------
  // Stage setup — used to be a "View switching" toggle between a
  // Technical Spec and a Studio Spotlight rig/camera. The Studio
  // Spotlight option was removed, so this just applies the one
  // remaining (Technical Spec) setup once at boot.
  // ---------------------------------------------------------------
  function applyView() {
    floor.visible = true;
    // chain/bail geometry stays out of the render — see the comment where
    // chainGroup is built, it only ever looked right under the old
    // close-up Spotlight camera.
    chainGroup.visible = false;
    dimLine.style.display = 'flex';
    camera.position.set(0, 10, 190);
    cameraFitActive = true;
  }

  // ---------------------------------------------------------------
  // Form wiring
  // ---------------------------------------------------------------
  var rebuildTimer = null;
  function scheduleRebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(rebuildPlate, 70);
  }

  // Floating Letters + Rail supports plain alphabet characters AND digits
  // as discrete cut pieces on the rail — no symbols (2026-09-14: user asked
  // to add 0-9; digits close their loops the same way letters like O/A/B/D
  // already do on this build, so there's no new structural issue digits
  // introduce that the rail-fusion logic doesn't already handle). The other
  // construction styles (engraved / cut-out / wire) keep the full
  // character set (digits + symbols). Reads the Construction row's active
  // button directly (same "observed via the DOM" approach the v2 pricing
  // script uses), rather than sharing state across the two scripts.
  var ALLOWED_ALPHANUMERIC = /^[A-Za-z0-9 ]*$/;
  // Connected Letters is stricter than Floating + Rail: no spaces either,
  // since a space would break the touching-letters chain into two pieces.
  var ALLOWED_CONNECTED = /^[A-Za-z0-9]*$/;
  function currentConstructionKey() {
    var b = constructionRowEl && constructionRowEl.querySelector('.opt.active');
    return b ? b.getAttribute('data-construction') : 'rail';
  }
  function isRailConstruction() {
    return currentConstructionKey() === 'rail';
  }
  // Case lock — REMOVED 2026-09-22 ("Now the capital alphabets have been
  // fixed, how about we add small letters and try to fix them as well").
  // Floating + Rail and Connected Letters used to force every letter to
  // capitals, because a lowercase "i" or "j" draws its dot as a completely
  // separate closed shape from the stem — with no bridge between them, that
  // dot had nothing holding it on and would be an orphan sliver that falls
  // off the physical cut. A survey of every character these two
  // constructions actually allow turned up the same defect on more than
  // i/j: Technical and Pirata One's "0" floats its centre dot free inside
  // the digit's own counter, and Cinzel's "K"/"k" doesn't quite connect its
  // diagonal leg to the stem. Rather than keep working around this by
  // blocking lowercase, `bridgeGlyphParts()` (above buildConnectedLetters)
  // now adds a small connecting bridge wherever a character's own shapes
  // don't already touch, so every one of these renders as one physically
  // joined piece — lowercase included. See buildConnectedLetters() and the
  // Rail branch in rebuildPlate() for where it's applied. `applyCaseLock`
  // is gone; `sanitizeName()` below only filters to each construction's
  // allowed character set now, case is whatever the customer typed.
  function sanitizeName(raw) {
    var v = raw.slice(0, MAX_CHARS);
    var key = currentConstructionKey();
    if (key === 'connected') {
      if (!ALLOWED_CONNECTED.test(v)) v = v.replace(/[^A-Za-z0-9]/g, '');
    } else if (key === 'rail') {
      if (!ALLOWED_ALPHANUMERIC.test(v)) v = v.replace(/[^A-Za-z0-9 ]/g, '');
    } else if (!ALLOWED.test(v)) {
      v = v.replace(/[^A-Za-z0-9 &'\-.,!?]/g, '');
    }
    return v;
  }

  input.addEventListener('input', function () {
    var v = sanitizeName(input.value);
    input.value = v;
    charcount.textContent = v.length;
    scheduleRebuild();
  });

  // Re-validate the existing text whenever Construction changes — e.g. if
  // "AB12" was typed under Engraved Plate and the customer then switches
  // to Floating + Rail, the digits need to drop immediately rather than
  // silently staying in a name that build can't actually cut. Reads the
  // clicked button's own data-construction directly (rather than going
  // through isRailConstruction()'s ".opt.active" lookup) because this
  // script's listener and the v2 pricing script's listener are both on
  // the same row — depending on registration order, the ".active" class
  // may not have moved to the new button yet when this runs.
  if (constructionRowEl) {
    constructionRowEl.addEventListener('click', function (e) {
      var btn = e.target.closest('.opt');
      if (!btn) return;
      var v = input.value.slice(0, MAX_CHARS);
      var clickedKey = btn.getAttribute('data-construction');
      if (clickedKey === 'connected') {
        if (!ALLOWED_CONNECTED.test(v)) v = v.replace(/[^A-Za-z0-9]/g, '');
      } else if (clickedKey === 'rail') {
        if (!ALLOWED_ALPHANUMERIC.test(v)) v = v.replace(/[^A-Za-z0-9 ]/g, '');
      } else if (!ALLOWED.test(v)) {
        v = v.replace(/[^A-Za-z0-9 &'\-.,!?]/g, '');
      }
      if (v !== input.value) {
        input.value = v;
        charcount.textContent = v.length;
      }
      scheduleRebuild();
    });
  }

  fontBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      fontBtns.forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');
      currentFontKey = btn.getAttribute('data-font');
      fontNote.textContent = FONTS[currentFontKey].note;
      rebuildPlate();
    });
  });

  finishBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      finishBtns.forEach(function (b) { b.classList.remove('active'); });
      btn.classList.add('active');
      currentFinishKey = btn.getAttribute('data-finish');
      rebuildPlate();
    });
  });

  // ---------------------------------------------------------------
  // Resize
  // ---------------------------------------------------------------
  function resize() {
    var w = stageWrap.clientWidth, h = stageWrap.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener('resize', resize);

  // ---------------------------------------------------------------
  // Dimension-line overlay (Technical Spec view)
  // ---------------------------------------------------------------
  function updateDimLine() {
    var half = lastWidth / 2;
    var y = lastBottomY - 6;
    var p1 = new THREE.Vector3(-half, y, 0);
    var p2 = new THREE.Vector3(half, y, 0);
    p1.project(camera); p2.project(camera);
    var w = stageWrap.clientWidth, h = stageWrap.clientHeight;
    var x1 = (p1.x * 0.5 + 0.5) * w, y1 = (1 - (p1.y * 0.5 + 0.5)) * h;
    var x2 = (p2.x * 0.5 + 0.5) * w, y2 = (1 - (p2.y * 0.5 + 0.5)) * h;
    var y = Math.max(y1, y2) + 26;
    dimLine.style.left = Math.min(x1, x2) + 'px';
    dimLine.style.top = y + 'px';
    dimLine.style.width = Math.abs(x2 - x1) + 'px';
  }

  // ---------------------------------------------------------------
  // Main loop
  // ---------------------------------------------------------------
  var frames = 0, lastFpsT = performance.now();
  function tick(t) {
    if (cameraFitActive) approachDesiredDistance();
    controls.update();
    renderer.render(scene, camera);
    updateDimLine();
    frames++;
    if (t - lastFpsT > 500) {
      if (fpsEl) fpsEl.textContent = Math.round((frames * 1000) / (t - lastFpsT));
      frames = 0; lastFpsT = t;
    }
    requestAnimationFrame(tick);
  }

  // ---------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------
  function boot() {
    resize();
    rebuildPlate();
    applyView();
    charcount.textContent = input.value.length;
    if (loadingEl) loadingEl.style.display = 'none';
    requestAnimationFrame(tick);
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(boot);
  } else {
    boot();
  }
})();